// HTTP APIクライアント
// Web環境で使用するHTTP API呼び出しを実装

import type { ApiResponse } from '../types/api';
import * as endpoints from './api-endpoints';

// ============================================================================
// 設定
// ============================================================================

// react-scriptsではprocess.envを使用
// react-scriptsはビルド時にprocess.env.REACT_APP_*を検索してコードに埋め込む
// ブラウザの実行時にはprocess.envは存在しないため、ビルド時に埋め込まれた値を使用
const getApiBaseUrl = (): string => {
  // react-scripts環境変数（ビルド時に文字列リテラルに置き換えられる）
  // @ts-ignore - react-scriptsがビルド時にprocess.envを置き換える
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  
  if (envUrl && envUrl !== 'undefined' && envUrl.trim() !== '') {
    console.log('[API Client] Using REACT_APP_API_BASE_URL:', envUrl);
    return envUrl;
  }
  
  // グローバル変数（ビルド時に注入）
  if (typeof window !== 'undefined' && (window as any).__API_BASE_URL__) {
    const url = (window as any).__API_BASE_URL__;
    console.log('[API Client] Using __API_BASE_URL__:', url);
    return url;
  }
  
  console.warn('[API Client] API_BASE_URL is not set. Using default: http://localhost:3000');
  console.warn('[API Client] Please set REACT_APP_API_BASE_URL environment variable in Cloudflare Pages settings');
  console.warn('[API Client] Current process.env.REACT_APP_API_BASE_URL:', envUrl);
  return 'http://localhost:3000';
};

const DEFAULT_TIMEOUT = 30000; // 30秒

// ============================================================================
// エラーハンドリング
// ============================================================================

class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * HTTPエラーをApiResponseに変換
 */
function handleHttpError(error: unknown): ApiResponse {
  if (error instanceof ApiError) {
    return {
      success: false,
      error: error.message,
      message: error.response?.message
    };
  }
  if (error instanceof Error) {
    return {
      success: false,
      error: error.message
    };
  }
  return {
    success: false,
    error: 'Unknown error occurred'
  };
}

// ============================================================================
// HTTP API呼び出し
// ============================================================================

/**
 * Tauriコマンド名をHTTPエンドポイントにマッピング
 */
function mapCommandToEndpoint(command: string, args?: Record<string, any>): {
  method: string;
  url: string;
  body?: any;
} {
  // 実行時にAPI_BASE_URLを取得
  const API_BASE_URL = getApiBaseUrl();
  
  // チャット管理API
  if (command === 'get_chat_list') {
    return { method: 'GET', url: `${API_BASE_URL}${endpoints.ChatEndpoints.list()}` };
  }
  if (command === 'get_chat_session') {
    return { method: 'GET', url: `${API_BASE_URL}${endpoints.ChatEndpoints.get(args?.chatId)}` };
  }
  if (command === 'create_chat_session') {
    return { method: 'POST', url: `${API_BASE_URL}${endpoints.ChatEndpoints.create()}` };
  }
  if (command === 'delete_chat') {
    return { method: 'DELETE', url: `${API_BASE_URL}${endpoints.ChatEndpoints.delete(args?.chatId)}` };
  }
  if (command === 'get_chat_display_messages') {
    return { method: 'GET', url: `${API_BASE_URL}${endpoints.ChatEndpoints.getMessages(args?.chatId)}` };
  }
  if (command === 'send_message') {
    return {
      method: 'POST',
      url: `${API_BASE_URL}${endpoints.ChatEndpoints.sendMessage(args?.chatId)}`,
      body: { content: args?.content }
    };
  }
  if (command === 'start_ai_generation') {
    return {
      method: 'POST',
      url: `${API_BASE_URL}${endpoints.ChatEndpoints.startGeneration(args?.chatId)}`,
      body: { userMessage: args?.userMessage }
    };
  }
  if (command === 'generate_ai_response') {
    return {
      method: 'POST',
      url: `${API_BASE_URL}${endpoints.ChatEndpoints.generateSync(args?.chatId)}`
    };
  }

  // セッション管理API
  if (command === 'create_session') {
    return {
      method: 'POST',
      url: `${API_BASE_URL}${endpoints.SessionEndpoints.create()}`,
      body: { prompt: args?.prompt }
    };
  }
  if (command === 'get_session_list') {
    return { method: 'GET', url: `${API_BASE_URL}${endpoints.SessionEndpoints.list()}` };
  }
  if (command === 'delete_session') {
    return { method: 'DELETE', url: `${API_BASE_URL}${endpoints.SessionEndpoints.delete(args?.sessionId)}` };
  }
  if (command === 'check_generation_status') {
    return { method: 'GET', url: `${API_BASE_URL}${endpoints.SessionEndpoints.checkGenerationStatus(args?.sessionId)}` };
  }
  if (command === 'continue_generation') {
    return { method: 'POST', url: `${API_BASE_URL}${endpoints.SessionEndpoints.continueGeneration(args?.sessionId)}` };
  }
  if (command === 'get_events') {
    return { method: 'GET', url: `${API_BASE_URL}${endpoints.SessionEndpoints.getEvents(args?.sessionId, args?.since)}` };
  }

  // ツリー操作API
  if (command === 'get_tree_structure') {
    return { method: 'GET', url: `${API_BASE_URL}${endpoints.TreeEndpoints.getStructure(args?.sessionId)}` };
  }
  if (command === 'add_node') {
    return {
      method: 'POST',
      url: `${API_BASE_URL}${endpoints.TreeEndpoints.addNode(args?.sessionId)}`,
      body: { parentId: args?.parentId, word: args?.word }
    };
  }
  if (command === 'delete_node') {
    return {
      method: 'DELETE',
      url: `${API_BASE_URL}${endpoints.TreeEndpoints.deleteNode(args?.sessionId, args?.nodeId)}`
    };
  }
  if (command === 'select_node') {
    return {
      method: 'POST',
      url: `${API_BASE_URL}${endpoints.TreeEndpoints.selectNode(args?.sessionId, args?.nodeId)}`
    };
  }
  if (command === 'ai_generate_x10') {
    return {
      method: 'POST',
      url: `${API_BASE_URL}${endpoints.TreeEndpoints.generateX10(args?.sessionId, args?.nodeId)}`
    };
  }
  if (command === 'ai_generate_x60') {
    return {
      method: 'POST',
      url: `${API_BASE_URL}${endpoints.TreeEndpoints.generateX60(args?.sessionId, args?.nodeId)}`
    };
  }

  // 設定管理API
  if (command === 'get_default_prob') {
    return { method: 'GET', url: `${API_BASE_URL}${endpoints.SettingsEndpoints.getDefaultProb()}` };
  }
  if (command === 'set_default_prob') {
    return {
      method: 'PUT',
      url: `${API_BASE_URL}${endpoints.SettingsEndpoints.setDefaultProb()}`,
      body: { prob: args?.prob }
    };
  }
  if (command === 'get_model_name') {
    return { method: 'GET', url: `${API_BASE_URL}${endpoints.SettingsEndpoints.getModelName()}` };
  }
  if (command === 'set_model_name') {
    return {
      method: 'PUT',
      url: `${API_BASE_URL}${endpoints.SettingsEndpoints.setModelName()}`,
      body: { modelName: args?.modelName }
    };
  }
  if (command === 'get_model_list') {
    return { method: 'GET', url: `${API_BASE_URL}${endpoints.SettingsEndpoints.getModelList()}` };
  }
  if (command === 'get_model_tags_config') {
    return {
      method: 'GET',
      url: `${API_BASE_URL}${endpoints.SettingsEndpoints.getModelTags(args?.modelName)}`
    };
  }

  // 未対応のコマンド
  throw new ApiError(`Unsupported command: ${command}`);
}

/**
 * HTTP APIを呼び出す
 */
export async function callHttpApi<T>(
  command: string,
  args?: Record<string, any>
): Promise<ApiResponse<T>> {
  try {
    const { method, url, body } = mapCommandToEndpoint(command, args);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);

    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new ApiError(
        `HTTP error ${response.status}: ${errorText}`,
        response.status,
        { message: errorText }
      );
    }

    const data = await response.json();
    return data as ApiResponse<T>;
  } catch (error) {
    if (error instanceof ApiError) {
      return handleHttpError(error);
    }
    if (error instanceof Error && error.name === 'AbortError') {
      return {
        success: false,
        error: 'Request timeout'
      };
    }
    return handleHttpError(error);
  }
}

/**
 * リトライ付きHTTP API呼び出し
 */
export async function callHttpApiWithRetry<T>(
  command: string,
  args?: Record<string, any>,
  maxRetries: number = 3
): Promise<ApiResponse<T>> {
  let lastError: ApiResponse<T> | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const result = await callHttpApi<T>(command, args);
    
    if (result.success) {
      return result;
    }

    lastError = result;

    // リトライしないエラー（4xx系のクライアントエラー）
    if (lastError.error?.includes('HTTP error 4')) {
      break;
    }

    // 指数バックオフでリトライ
    if (attempt < maxRetries - 1) {
      const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  return lastError || {
    success: false,
    error: 'Max retries exceeded'
  };
}

